import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BookOpen, Palette, Music, Users, Brain, Heart, Sparkles, ArrowRight } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const Programs = () => {
  const programs = [
    {
      name: 'Nursery',
      color: 'from-orange-400 to-yellow-400',
      bgColor: 'bg-gradient-to-br from-orange-50 to-yellow-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-600',
      description: 'The perfect first step into the world of learning! Our Nursery program introduces young children to the joy of school in a gentle, play-based environment.',
      approach: 'Play-based learning with sensory activities, songs, rhymes, and simple crafts. We focus on making school a happy, safe place for your little one.',
      skills: [
        'Social interaction & sharing',
        'Basic communication skills',
        'Motor skill development',
        'Color & shape recognition',
        'Following simple routines',
        'Creative expression through art',
      ],
      schedule: '3 hours/day, 5 days/week',
      icon: Heart,
    },
    {
      name: 'LKG',
      color: 'from-teal-400 to-cyan-400',
      bgColor: 'bg-gradient-to-br from-teal-50 to-cyan-50',
      borderColor: 'border-teal-200',
      textColor: 'text-teal-600',
      description: 'Building on the foundation, LKG introduces structured learning while maintaining the fun! Children begin their journey with letters, numbers, and more.',
      approach: 'Activity-based learning with phonics introduction, number concepts, and thematic units. We use stories, games, and hands-on activities to make learning engaging.',
      skills: [
        'Phonics & letter recognition',
        'Numbers 1-20 & counting',
        'Pre-writing skills',
        'Vocabulary building',
        'Problem-solving basics',
        'Social & emotional development',
      ],
      schedule: '3.5 hours/day, 5 days/week',
      icon: BookOpen,
    },
    {
      name: 'UKG',
      color: 'from-pink-400 to-purple-400',
      bgColor: 'bg-gradient-to-br from-pink-50 to-purple-50',
      borderColor: 'border-pink-200',
      textColor: 'text-pink-600',
      description: 'Preparing children for primary school with confidence! UKG focuses on academic readiness while nurturing creativity and independent thinking.',
      approach: 'Structured curriculum with reading readiness, math concepts, science exploration, and environmental awareness. We prepare children for a smooth transition to primary school.',
      skills: [
        'Reading simple words & sentences',
        'Writing letters & numbers',
        'Basic addition & subtraction',
        'Science & nature exploration',
        'Critical thinking skills',
        'Independence & responsibility',
      ],
      schedule: '4 hours/day, 5 days/week',
      icon: Sparkles,
    },
  ];

  const commonActivities = [
    { icon: Palette, title: 'Arts & Crafts', description: 'Creative expression through painting, drawing, and crafting', color: 'from-pink-400 to-rose-500' },
    { icon: Music, title: 'Music & Dance', description: 'Rhymes, songs, and movement activities for joyful learning', color: 'from-purple-400 to-indigo-500' },
    { icon: Users, title: 'Group Activities', description: 'Collaborative games that build social skills and teamwork', color: 'from-cyan-400 to-blue-500' },
    { icon: Brain, title: 'Brain Games', description: 'Puzzles and activities that develop cognitive abilities', color: 'from-orange-400 to-amber-500' },
  ];

  return (
    <div className="min-h-screen pt-28 bg-gradient-to-br from-yellow-50 via-pink-50 to-cyan-50" data-testid="programs-page">
      {/* Hero Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center">
            <span className="font-accent text-xl text-purple-500 inline-block -rotate-2 mb-2">
              Our Programs
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 bg-clip-text text-transparent mb-6">
              Learning Programs
            </h1>
            <p className="font-body text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Each program is carefully designed to meet the developmental needs of children at every stage, ensuring a strong foundation for future learning.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16 md:space-y-24">
            {programs.map((program, index) => (
              <motion.div
                key={program.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`${program.bgColor} rounded-[2.5rem] p-8 md:p-12 lg:p-16 ${program.borderColor} border-2 shadow-xl`}
                data-testid={`program-${program.name.toLowerCase()}`}
              >
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                  {/* Left Column - Info */}
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`w-16 h-16 bg-gradient-to-br ${program.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                        <program.icon className="w-8 h-8 text-white" strokeWidth={2.5} />
                      </div>
                      <div>
                        <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-800">
                          {program.name}
                        </h2>
                      </div>
                    </div>

                    <p className="font-body text-lg text-slate-700 leading-relaxed mb-6">
                      {program.description}
                    </p>

                    <div className="mb-6">
                      <h3 className="font-heading text-xl font-semibold text-slate-800 mb-3">
                        Learning Approach
                      </h3>
                      <p className="font-body text-slate-600 leading-relaxed">
                        {program.approach}
                      </p>
                    </div>

                    <div className={`inline-flex items-center gap-2 px-4 py-2 bg-white/70 rounded-full font-body text-sm ${program.textColor} font-semibold shadow-sm`}>
                      <span>Schedule:</span>
                      <span>{program.schedule}</span>
                    </div>
                  </div>

                  {/* Right Column - Skills */}
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <div className="bg-white/80 backdrop-blur-sm rounded-[2rem] p-8 h-full shadow-lg border border-white">
                      <h3 className="font-heading text-xl font-semibold text-slate-800 mb-6">
                        Skills Developed
                      </h3>
                      <ul className="space-y-4">
                        {program.skills.map((skill, skillIndex) => (
                          <li key={skillIndex} className="flex items-start gap-3">
                            <div className={`w-6 h-6 bg-gradient-to-br ${program.color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm`}>
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <span className="font-body text-slate-700">{skill}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Activities */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="font-accent text-xl text-cyan-500 inline-block -rotate-2 mb-2">
              Beyond Academics
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-4">
              Activities for All Programs
            </h2>
            <p className="font-body text-lg text-slate-600 max-w-2xl mx-auto">
              Every child participates in enriching activities that complement classroom learning
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {commonActivities.map((activity, index) => (
              <motion.div
                key={activity.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-[2rem] border border-purple-100 text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-xl group shadow-lg"
                data-testid={`activity-${activity.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${activity.color} rounded-2xl flex items-center justify-center mx-auto mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6 shadow-lg`}>
                  <activity.icon className="w-8 h-8 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-800 mb-3">
                  {activity.title}
                </h3>
                <p className="font-body text-slate-600">
                  {activity.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Find the Perfect Program for Your Child
            </h2>
            <p className="font-body text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              Not sure which program is right for your little one? We're here to help! Contact us for a personalized consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/admissions"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-purple-600 font-body font-bold rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg active:translate-y-0 active:scale-95"
                data-testid="programs-cta-admissions"
              >
                Apply Now
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-white font-body font-bold rounded-full border-2 border-white transition-all duration-300 hover:-translate-y-1 hover:bg-white/10 active:translate-y-0 active:scale-95"
                data-testid="programs-cta-contact"
              >
                Schedule a Visit
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
